/**
 * Maps a power-level profile SUGGESTION id (`details.powerLevel.profile.improve`)
 * to the upgrade search that addresses it — the bridge that turns "this is thin"
 * into "click for cards to fix it". Keyed by the suggestion ids the power scorer
 * emits (see POWER_PROFILE_COPY.improve in data/deckProfileMarkers.js), mirroring
 * MANABASE_PROFILE_UPGRADES below so power suggestions get the same one-click CTAs.
 *
 * Each value is `{ preset, title }`, passed straight to <UpgradelizerPane preset>.
 * Most map to a CLASSIC role category, which fires regardless of commander tuned
 * stats. The wincon / combo suggestions use `combos` mode, which needs tuned
 * stats — the CTA degrades to plain text (fallbackRender) when unavailable.
 */

// Reusable preset builders keep the maps declarative and de-duplicated.
const role = (category) => ({ mode: 'role', category });
const combosWincons = { mode: 'combos', comboWinconOnly: true };
const combos = { mode: 'combos' };

export const POWER_PROFILE_UPGRADES = {
    // Closing the game (combos mode — needs tuned stats, self-gates)
    addWincon: { preset: combosWincons, title: 'Win conditions' },
    tightenCombo: { preset: combos, title: 'Combo lines' },
    // Consistency
    addTutors: { preset: role('TUTOR'), title: 'Tutors' },
    // Interaction
    addInteraction: { preset: role('SPOT_REMOVAL'), title: 'Interaction' },
    addSpotRemoval: { preset: role('SPOT_REMOVAL'), title: 'Spot removal' },
    addBoardWipes: { preset: role('BOARD_WIPES'), title: 'Board wipes' },
    addCounterspells: { preset: role('COUNTERSPELL'), title: 'Counterspells' },
    addGraveyardHate: { preset: role('GRAVEYARD_HATE'), title: 'Graveyard hate' },
    // Engine / mana
    addCardAdvantage: { preset: role('CANTRIP'), title: 'Card advantage' },
    addFastMana: { preset: role('FASTMANA'), title: 'Fast mana' },
    addRamp: { preset: role('RAMP'), title: 'Ramp' },
    addRecursion: { preset: role('RECURSION'), title: 'Recursion' },
};

/** The upgrade spec for a power suggestion id, or null when not actionable. */
export function getPowerUpgrade(id) {
    if (!id) return null;
    return POWER_PROFILE_UPGRADES[id] || null;
}

/**
 * Manabase profile risk / suggestion ids → the upgrade search that addresses
 * them. Keyed by the ids in MANABASE_PROFILE_COPY (risks + improve) in
 * ManabasePane.jsx, so every diagnosed item becomes a one-click search and
 * new scorer ids light up automatically.
 *
 * All presets use the CLASSIC card_category path (no commander stats). Land
 * searches are now category-LESS — they're specified by the structural
 * land filters from the quality overhaul (landCycle / entersUntapped /
 * producedColorsSubset, scoped to the LAND type) and the endpoint ranks them
 * by quality automatically. `ramp` / `fastmana` are genuine categories
 * (rocks/dorks/lands and Tomb-style fast mana) so they stay category-only.
 */
// The land-quality overhaul (LandClassifier + structural index fields) lets
// us target the EXACT fix the scorer diagnosed, ranked by quality, instead of
// filtering the budget rainbow junk by hand. No category needed — the search
// is fully specified by `landCycle`/`entersUntapped`/`producedColorsSubset`,
// which the endpoint accepts category-less (it auto-sorts by quality and
// auto-defaults colorFilters from the deck's identity).
//
// The premium dual cycles — shared by the quality-lands presets below.
const PREMIUM_DUAL_CYCLES = ['dual_abur', 'shock', 'check', 'fast', 'pain', 'triome'];
// "Need more lands" — premium duals PLUS fetches (anything that raises the
// count with quality fixing).
const qualityLands = {
    mode: 'role',
    selectedTypes: ['LAND'],
    advancedFilters: {
        landCycle: ['fetch', ...PREMIUM_DUAL_CYCLES],
        entersUntapped: true,
        producedColorsSubset: true,
        sortBy: 'quality',
    },
};
// "Add multicolor lands / color fixing" — the direct fix for a multi-color deck
// leaning on basics (few lands flex color). `fixesColorsMin: 2` is the new
// structured filter for "produces 2+ colors", so this returns the duals /
// shocks / triomes / battle-lands etc. that actually make more than one color,
// on-color (producedColorsSubset) and premium-tier. No entersUntapped gate —
// for a color-fixing concern, a tapped on-color dual still fixes.
const multiColorLands = {
    mode: 'role',
    selectedTypes: ['LAND'],
    advancedFilters: {
        fixesColorsMin: 2,
        producedColorsSubset: true,
        qualityTier: ['premium', 'solid'],
        sortBy: 'quality',
    },
};
// Untapped fixing — the fix for tap-land-heavy / slow manabases: any untapped
// on-color land, with the rainbow/tapland/utility junk explicitly dropped.
const untappedFixing = {
    mode: 'role',
    selectedTypes: ['LAND'],
    advancedFilters: {
        entersUntapped: true,
        excludeLandCycle: ['rainbow', 'tapland', 'utility'],
        producedColorsSubset: true,
        sortBy: 'quality',
    },
};
// Premium fetches ONLY — the dedicated `fetch` cycle (true dual-fetches like
// Polluted Delta / Wooded Foothills), NOT basic-fetchers (Prismatic Vista,
// Evolving Wilds, Panoramas). `producedColorsSubset` drops off-color fetches
// via the backend's fetch-target-color awareness (Polluted Delta dropped for
// RG, Wooded Foothills kept).
const fetchLands = {
    mode: 'role',
    selectedTypes: ['LAND'],
    advancedFilters: {
        landCycle: ['fetch'],
        qualityTier: ['premium', 'solid'],
        entersUntapped: true,
        producedColorsSubset: true,
        sortBy: 'quality',
    },
};
// Utility lands — for when the scorer wants more non-mana land effects.
const utilityLands = {
    mode: 'role',
    selectedTypes: ['LAND'],
    advancedFilters: { isUtilityLand: true, sortBy: 'quality' },
};
// MDFC lands — modal spell-or-land cards (Pathways, Bala Ged Recovery,
// Agadeem's Awakening). `isMdfcLand` is the only way to catch spell-front
// MDFCs (no land baseTypes), so NO `selectedTypes: LAND` here — that would
// drop the spell-front ones. Color identity already scopes to the deck.
const mdfcLands = {
    mode: 'role',
    advancedFilters: { isMdfcLand: true, sortBy: 'quality' },
};
// Mono-color manabases have no duals/fetches to FIX with — color fixing is a
// non-problem. The actual upgrade is the best untapped on-color lands, which
// for a single color means the premium utility / value lands (Castle Garenbrig,
// Boseiju, Nykthos…). So: untapped + on-color + premium-tier, no dual-cycle
// restriction, and we KEEP utility lands (unlike untappedFixing) since those
// are the upgrade here.
const monoQualityLands = {
    mode: 'role',
    selectedTypes: ['LAND'],
    advancedFilters: {
        entersUntapped: true,
        producedColorsSubset: true,
        qualityTier: ['premium', 'solid'],
        excludeLandCycle: ['rainbow', 'tapland', 'bounce'],
        sortBy: 'quality',
    },
};

// ONLY the SUGGESTIONS column ("changes that would raise the score") is
// actionable — each id maps to the search that makes that change. RISKS
// ("concerns dragging the score") are diagnostic and render as plain text, so
// they're intentionally NOT in this map: a risk like "Tap-land heavy" would
// just duplicate the "Remove tap lands" suggestion, and concerns like "low
// curve / tempo mismatch" or "commander pip demand" aren't fixable by a single
// card search. Every actionable risk already has a suggestion counterpart
// (landCountLow→increaseLandCount, tapLandHeavy→removeTapLands, etc.), so no
// actionable link is lost.
export const MANABASE_PROFILE_UPGRADES = {
    addFetches: { preset: fetchLands, title: 'Fetch lands' },
    addPremiumDuals: { preset: multiColorLands, title: 'Multicolor lands' },
    addFastManaLands: { preset: role('FASTMANA'), title: 'Fast mana' },
    removeTapLands: { preset: untappedFixing, title: 'Untapped lands' },
    addUtilityLands: { preset: utilityLands, title: 'Utility lands' },
    addColorFixing: { preset: multiColorLands, title: 'Multicolor lands' },
    increaseLandCount: { preset: qualityLands, title: 'Quality lands' },
    increaseRamp: { preset: role('RAMP'), title: 'Ramp' },
    // shoreUpColor is handled dynamically in getManabaseUpgrade (color-specific).
    diversifyColorSources: { preset: multiColorLands, title: 'Multicolor lands' },
    addMdfcLands: { preset: mdfcLands, title: 'MDFC lands' },
};

// Entries whose preset assumes 2+ colors (duals / fetches / color fixing).
// On a mono-color deck these are meaningless — route them to the mono
// "best untapped on-color lands" search instead.
// Suggestion ids whose preset assumes 2+ colors (duals / color fixing) or
// excludes utility lands. On a mono-color deck those are meaningless — route
// them to the mono "best untapped on-color lands" search (which keeps the
// utility/value lands that ARE the upgrade for a single color).
const MONO_LAND_OVERRIDE_IDS = new Set([
    'addColorFixing',
    'addPremiumDuals',
    'increaseLandCount',
    'diversifyColorSources',
    'removeTapLands',
]);

const COLOR_SOURCE_LABELS = { W: 'White', U: 'Blue', B: 'Black', R: 'Red', G: 'Green' };

/**
 * The upgrade spec for a manabase risk/suggestion id, or null.
 * `colorCount` adapts the land searches: a mono-color deck (≤1) has no
 * duals/fetches to fix with, so color-fixing entries swap to a mono-friendly
 * quality-lands search. `data` carries the scorer marker's payload (e.g. the
 * under-produced color for shoreUpColor).
 */
export function getManabaseUpgrade(id, { colorCount, data } = {}) {
    if (!id) return null;
    // "Shore up <color>" — the deck under-produces ONE color. The fix is more
    // producers of that color of ANY card type (lands, rocks, dorks, …), so we
    // filter on `producesMana` (not lands) and exclude fast mana. The color is
    // marker-specific, so the preset is built here from data.color.
    if (id === 'shoreUpColor') {
        const color = String(data?.color || '').toUpperCase();
        if (!color || !'WUBRG'.includes(color)) return null;
        return {
            title: `${COLOR_SOURCE_LABELS[color]} sources`,
            preset: {
                mode: 'role',
                advancedFilters: {
                    producesMana: [color],
                    isFastMana: false,
                    // Drop any-color rainbow lands (City of Brass / Mana
                    // Confluence) — they're not the targeted fix.
                    excludeLandCycle: ['rainbow'],
                    // NOT sortBy:'quality' — qualityRank is 0 for every non-land,
                    // so quality-sort buries all rocks/dorks below the lands.
                    // `salt` ranks across types so producers of every type show.
                    sortBy: 'salt',
                },
            },
        };
    }
    const base = MANABASE_PROFILE_UPGRADES[id] || null;
    if (!base) return null;
    if (colorCount != null && colorCount <= 1 && MONO_LAND_OVERRIDE_IDS.has(id)) {
        return { preset: monoQualityLands, title: 'Quality lands' };
    }
    return base;
}

// These scorer suggestions all mean "add lands that make 2+ of your colors" and
// now open the same multicolor-lands search — showing all of them is three ways
// of saying the same thing. Collapse to the first (clearest) one present.
const MULTICOLOR_FIX_PRIORITY = ['addPremiumDuals', 'addColorFixing', 'diversifyColorSources'];

/** Drop redundant multicolor-fix suggestions, keeping the highest-priority one. */
export function dedupeManabaseSuggestions(items) {
    const present = MULTICOLOR_FIX_PRIORITY.filter((id) => items.some((it) => it.id === id));
    if (present.length <= 1) return items;
    const drop = new Set(present.slice(1));
    return items.filter((it) => !drop.has(it.id));
}
